import { useCallback, useEffect, useId, useRef, useState } from "react"
import { createPortal } from "react-dom"
import cc from "classcat"
import { XMarkIcon } from "@heroicons/react/20/solid"
import useTranslations from "hooks/useTranslations"
import { fillWithUtm } from "utils/url"

/**
 * The page's only call to action: cal.com, opened over the page rather than
 * navigated to.
 *
 * The rest of the site books meetings with `RequestButton`, which is a plain
 * outbound link — clicking it leaves somebuddy.com for cal.com. That is fine on
 * a page someone arrived at deliberately; it is expensive on a page taking paid
 * traffic, where sending the visitor away mid-pitch means they finish reading
 * the pitch never. So this keeps them here and puts the booker in a dialog.
 *
 * It is still a real anchor with a real href. Cmd-click, middle-click and
 * "copy link" behave normally, and with JavaScript off it degrades to exactly
 * what the rest of the site does. `preventDefault` only fires on a plain click.
 */

const CAL_EMBED_QUERY = "embed=uk-schools&layout=month_view"

/** cal.com's own embed reads its config from the query string, so the booker
 *  renders without the marketing site's header and footer. Any utm codes the
 *  visitor arrived with ride along, which is how a LinkedIn click stays
 *  attributable all the way to a booking. */
const toEmbedUrl = (link: string) => {
  const withUtm = fillWithUtm(link)
  return `${withUtm}${withUtm.includes("?") ? "&" : "?"}${CAL_EMBED_QUERY}`
}

type Props = {
  label: string
  /** `black` on light grounds, `green` on the dark CTA band. */
  bgStyle?: "black" | "green"
  className?: string
}

export default function BookDemoButton({
  label,
  bgStyle = "black",
  className,
}: Props) {
  const { appointmentLink, ukSchoolsBookerTitle, ukSchoolsBookerLead } =
    useTranslations()
  const titleId = useId()
  const [open, setOpen] = useState(false)
  const triggerRef = useRef<HTMLAnchorElement>(null)
  const closeRef = useRef<HTMLButtonElement>(null)

  const close = useCallback(() => {
    setOpen(false)
    // Send focus back where it came from, or a keyboard user is dumped at the
    // top of the document with no idea what just happened.
    triggerRef.current?.focus()
  }, [])

  useEffect(() => {
    if (!open) return undefined

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") close()
    }
    document.addEventListener("keydown", onKeyDown)

    // Without this the page behind the dialog scrolls under the pointer.
    const { overflow } = document.body.style
    document.body.style.overflow = "hidden"

    // `aria-modal` tells assistive tech to ignore the rest of the page but does
    // not move real keyboard focus, so without this Tab walks straight out of
    // the booker into the ~25 links and buttons behind it — invisible, but
    // focusable. `inert` is the right tool rather than a Tab-wrapping handler:
    // most of this dialog is a cross-origin iframe, and once focus is inside
    // it our keydown listener never fires, so a handler could not catch focus
    // leaving the far end of the booker. The dialog itself is portalled to
    // document.body and so sits outside this subtree, unaffected.
    const appRoot = document.getElementById("___gatsby")
    appRoot?.setAttribute("inert", "")

    closeRef.current?.focus()

    return () => {
      document.removeEventListener("keydown", onKeyDown)
      document.body.style.overflow = overflow
      appRoot?.removeAttribute("inert")
    }
  }, [open, close])

  const onClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    // Leave the modified clicks alone — they mean "open this somewhere else".
    if (
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey ||
      event.button !== 0
    ) {
      return
    }
    event.preventDefault()
    setOpen(true)
  }

  return (
    <>
      <a
        ref={triggerRef}
        href={fillWithUtm(appointmentLink)}
        onClick={onClick}
        className={cc([
          "pointer inline-block max-w-md whitespace-pre-wrap rounded-full border-2 border-solid px-6 py-3 text-center font-display text-base font-bold leading-snug no-underline transition duration-150 ease-in-out focus:outline-none focus-visible:ring-4 active:scale-[0.96]",
          {
            "border-zinc-800 bg-zinc-800 text-white focus-visible:ring-zinc-500 active:border-zinc-700 active:bg-zinc-700 lg:py-4":
              bgStyle === "black",
            "border-kkm-green bg-kkm-green text-zinc-900 hover:bg-teal-300 focus-visible:ring-kkm-green/60 lg:py-4":
              bgStyle === "green",
          },
          className,
        ])}
      >
        {label}
      </a>

      {open && typeof document !== "undefined"
        ? createPortal(
            // Portalled to the body on purpose. `position: fixed` is measured
            // against the nearest transformed ancestor rather than the
            // viewport, and this page is full of framer-motion wrappers that
            // set `transform` — inside one, the dialog would be pinned to a
            // section instead of the screen.
            <div
              className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-900/70 p-4 backdrop-blur-sm"
              onClick={close}
              role="presentation"
            >
              {/* Wide enough for cal.com's month view to lay out its three
                  columns with its own margins — at 3xl the booker filled the
                  frame edge to edge and looked clipped. The height is capped
                  near the booker's natural height too, because cal centres
                  itself vertically and a taller frame just adds dead space. */}
              <div
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                className="relative flex h-[90vh] max-h-[44rem] w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-white p-2 shadow-2xl sm:p-4"
                onClick={(event) => event.stopPropagation()}
              >
                <button
                  ref={closeRef}
                  type="button"
                  onClick={close}
                  aria-label="Close"
                  className="absolute right-3 top-3 z-10 flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border-0 bg-zinc-900/80 text-white transition duration-150 ease-out hover:bg-zinc-900 focus:outline-none focus-visible:ring-4 focus-visible:ring-white active:scale-[0.96]"
                >
                  <XMarkIcon aria-hidden="true" className="h-5 w-5" />
                </button>

                {/* Kaisa: clicking "Get your 30 days free trial" and landing in
                    a bare calendar is disorienting — the button promised a
                    trial and the screen shows time slots. This says what the
                    meeting is for before cal.com loads. `pr-12` keeps the text
                    clear of the close button above it. */}
                <div className="shrink-0 px-3 pb-4 pr-12 pt-1 sm:px-4 sm:pr-14">
                  <h2
                    id={titleId}
                    className="m-0 font-display text-xl font-bold text-zinc-900 lg:text-2xl"
                  >
                    {ukSchoolsBookerTitle}
                  </h2>
                  {ukSchoolsBookerLead ? (
                    <p className="m-0 mt-1 text-sm leading-snug text-zinc-600 lg:text-base">
                      {ukSchoolsBookerLead}
                    </p>
                  ) : null}
                </div>

                {/* `min-h-0` so the iframe can shrink inside the flex column —
                    without it a flex child refuses to go below its content
                    height and pushes the dialog's bottom edge off-screen. */}
                <iframe
                  src={toEmbedUrl(appointmentLink)}
                  title={ukSchoolsBookerTitle || label}
                  className="min-h-0 w-full flex-1 rounded-xl border-0"
                />
              </div>
            </div>,
            document.body,
          )
        : null}
    </>
  )
}
